import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  makeStyles,
  Button,
  Tooltip,
  IconButton,
  TextField,
  ClickAwayListener,
  Paper,
  MenuList,
  MenuItem,
  Snackbar,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Alert } from "@material-ui/lab";

import {
  ExitToApp,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import LanguageSelector from "./LanguageSelector";
import ThemeSelector from "./ThemeSelector";
import { getApiBaseUrl } from "../config";
import CellSettingsModal from "./CellSettingsModal";

const toSortedCellOptions = (cells) =>
  cells
    .map((cell) => ({
      title: cell.name,
      value: cell.id,
    }))
    .sort((a, b) =>
      a.title.localeCompare(b.title, "pt-BR", { sensitivity: "base" }),
    );

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "var(--ikaro-text-white)",
    boxShadow: "var(--ikaro-shadow-light)",
    zIndex: theme.zIndex.drawer + 1,
  },
  logoText: {
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  },
  red: {
    color: theme.palette.primary.main,
  },
  gray: {
    color: "var(--ikaro-gray-dark)",
    marginLeft: theme.spacing(1),
    fontSize: "0.6rem",
    verticalAlign: "bottom",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    minHeight: "0px",
  },
  logoutButton: {
    marginLeft: theme.spacing(2),
    color: "var(--ikaro-text-white) !important",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
  },
  menuArea: {
    display: "flex",
    alignItems: "center",
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  cellSelector: {
    position: "relative",
    width: "300px",
    cursor: "pointer",
  },
  cellDisplay: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    border: "1px solid transparent",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    minHeight: "40px",
    height: "40px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
  },
  cellText: {
    flex: 1,
    color: "var(--ikaro-text-white)",
    fontSize: "0.875rem",
    fontWeight: "bold",
    textAlign: "left",
  },
  editMode: {
    position: "relative",
    width: "100%",
  },
  optionsList: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "200px",
    overflow: "auto",
    zIndex: 1000,
    boxShadow: theme.shadows[3],
    backgroundColor: "var(--ikaro-text-white)",
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: theme.shape.borderRadius,
  },
  optionItem: {
    padding: theme.spacing(1, 2),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  loadingOption: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  cellSettingsButton: {
    color: "var(--ikaro-text-white)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
    },
  },
}));

const Header = ({
  isAuthenticated,
  setIsAuthenticated,
  onMenuClick,
  menuOpen,
  embedMode = false,
}) => {
  const API_BASE_URL = getApiBaseUrl();

  const classes = useStyles();
  const { t } = useTranslation("header");
  const {
    token,
    cell,
    clearAuth,
    changeCell,
    user,
    validateAndAdjustCell,
    hasRole,
  } = useAuth();

  const [editMode, setEditMode] = React.useState(false);
  const [optionsCells, setOptionsCells] = React.useState([]);
  const [loadingCells, setLoadingCells] = React.useState(false);
  const [tempSearchTerm, setTempSearchTerm] = React.useState("");
  const [canCreateCell, setCanCreateCell] = React.useState(false);
  const [cellSettingsOpen, setCellSettingsOpen] = React.useState(false);

  // Toasts (Snackbar)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  const showToast = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Limpar estado de células quando usuário muda ou faz logout
  React.useEffect(() => {
    // Se não há usuário autenticado ou mudou o usuário, limpar estados
    if (!user || !token) {
      setEditMode(false);
      setOptionsCells([]);
      setLoadingCells(false);
      setTempSearchTerm("");
      setCanCreateCell(false);
    }
  }, [user, token]);

  // Garantir que optionsCells seja limpa quando o usuário muda
  React.useEffect(() => {
    // Sempre limpar a lista de células quando o usuário muda
    // para evitar reutilização de dados de usuários anteriores
    setOptionsCells([]);
    setCanCreateCell(false);
  }, [user?.email, user?.company_name]);

  // Função para buscar células
  const listCells = React.useCallback(
    async (searchQuery = "") => {
      try {
        const url = searchQuery
          ? `${API_BASE_URL}/cells?name=${encodeURIComponent(searchQuery)}`
          : `${API_BASE_URL}/cells`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          return await response.json();
        } else {
          const errorData = await response.json();
          console.error("Erro ao listar células:", errorData);
        }
      } catch (error) {
        console.error("Erro ao listar células:", error);
      }
    },
    [API_BASE_URL, token],
  );

  // Função para lidar com mudanças no input durante edição
  const handleInputChange = React.useCallback(
    async (newInputValue) => {
      if (!editMode || !user || !token) return;

      setTempSearchTerm(newInputValue);
      setLoadingCells(true);
      setCanCreateCell(false); // Reset inicial

      // Sempre limpar optionsCells antes de fazer nova busca para evitar
      // misturar dados de usuários diferentes
      setOptionsCells([]);

      const result = await listCells(newInputValue);
      if (result && result.data) {
        const newOptions = toSortedCellOptions(result.data);
        setOptionsCells(newOptions);

        // Verifica se não existe exatamente o mesmo texto em result.data
        const exists = result.data.some((cell) => cell.code === newInputValue);

        // Define se pode criar uma nova célula
        const canCreate =
          (result.data.length === 0 || !exists) &&
          newInputValue &&
          newInputValue.trim() !== "" &&
          user &&
          user.permissions &&
          hasRole("CELLS", "add_cells");

        console.log("Debug - canCreate:", canCreate, {
          dataLength: result.data.length,
          exists,
          newInputValue,
          hasPermission: hasRole("CELLS", "add_cells"),
          userEmail: user.email,
          companyName: user.company_name,
        });

        setCanCreateCell(canCreate);
      } else {
        // Se não há resultado da API, ainda assim verifica se pode criar
        const canCreate =
          newInputValue &&
          newInputValue.trim() !== "" &&
          user &&
          user.permissions &&
          hasRole("CELLS", "add_cells");
        setCanCreateCell(canCreate);
      }
      setLoadingCells(false);
    },
    [editMode, user, token, listCells, hasRole],
  );

  // Debounce para evitar muitas requisições
  const debouncedHandleInputChange = React.useMemo(() => {
    const debounce = (fn, delay) => {
      let timeoutId;
      return (...args) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => fn(...args), delay);
      };
    };
    return debounce(handleInputChange, 300);
  }, [handleInputChange]);

  const handleCreateNewCell = async (title) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cells/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: title }),
      });
      if (response.ok) {
        const newCell = await response.json();
        changeCell({ title: newCell.name, value: newCell.id });
        setEditMode(false);
        setTempSearchTerm("");
        setCanCreateCell(false);
        window.location.reload();
      } else {
        const errorData = await response.json();
        console.error("Erro ao criar nova célula:", errorData);
        alert(errorData.detail);
      }
    } catch (error) {
      alert(error);
      console.error("Erro ao criar nova célula:", error);
    }
  };

  // Função para o botão de criar célula
  const handleCreateButtonClick = () => {
    if (tempSearchTerm && tempSearchTerm.trim() !== "") {
      handleCreateNewCell(tempSearchTerm);
    }
  };

  // Função para entrar no modo de edição
  const handleStartEdit = async () => {
    if (!user || !token) return; // Garantir que há usuário autenticado

    setEditMode(true);
    setTempSearchTerm(cell ? cell.title : "");
    setLoadingCells(true);
    setCanCreateCell(false);

    // Limpar dados antigos antes de carregar novos para evitar
    // misturar células de usuários diferentes
    setOptionsCells([]);

    // Carrega todas as células quando entra no modo de edição
    const result = await listCells("");
    if (result && result.data) {
      const newOptions = toSortedCellOptions(result.data);
      setOptionsCells(newOptions);

      // Valida se a célula atual ainda existe na lista
      validateAndAdjustCell(newOptions);
    }
    setLoadingCells(false);
  };

  // Função para cancelar edição
  const handleCancelEdit = () => {
    setEditMode(false);
    setTempSearchTerm("");
    setOptionsCells([]);
    setCanCreateCell(false);
  };

  // Função para selecionar uma célula
  const handleSelectCell = (selectedCell) => {
    changeCell(selectedCell);
    setEditMode(false);
    setTempSearchTerm("");
    setOptionsCells([]);
    setCanCreateCell(false);
  };

  const handleLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
  };

  const canEditCell = !!user && !!token && hasRole("CELLS", "edit_cells");

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.menuArea}>
          {isAuthenticated && !embedMode && (
            <IconButton
              color="inherit"
              aria-label={menuOpen ? t("toggleMenuClose") : t("toggleMenuOpen")}
              edge="start"
              onClick={onMenuClick}
              className={classes.menuButton}
            >
              {menuOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Typography variant="h7" className={classes.logoText}>
            <img
              src="https://elaw.com.br/wp-content/uploads/2024/08/Logo.svg"
              alt="eLaw GenAI"
              className="header-logo"
            />
            <span className="flex-row items-end">&nbsp;GenAI</span>
          </Typography>
        </div>

        {isAuthenticated && (
          <Box className={classes.userInfo}>
            <Typography variant="body2" className="mr-8 text-white">
              {t("cellLabel")}
            </Typography>
            <ClickAwayListener
              onClickAway={editMode ? handleCancelEdit : () => {}}
            >
              <div className={classes.cellSelector} data-wizard="cell-selector">
                {!editMode ? (
                  // Modo de exibição (só leitura)
                  <div
                    className={classes.cellDisplay}
                    onClick={handleStartEdit}
                  >
                    <span className={classes.cellText}>
                      {cell ? cell.title : t("selectCell")}
                    </span>
                    <ExpandMoreIcon />
                  </div>
                ) : (
                  // Modo de edição
                  <div className={classes.editMode}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("searchCell")}
                      size="small"
                      value={tempSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTempSearchTerm(value);

                        // Não usar optionsCells antigos para validação imediata
                        // A validação será feita após a resposta da API no debouncedHandleInputChange
                        setCanCreateCell(false);

                        debouncedHandleInputChange(value);
                      }}
                      autoFocus
                      InputProps={{
                        className: "header-cell-input",
                        endAdornment: loadingCells ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : canCreateCell ? (
                          <Tooltip
                            title={t("createNewCell", { name: tempSearchTerm })}
                          >
                            <IconButton
                              size="small"
                              onClick={handleCreateButtonClick}
                              className="header-create-cell-btn"
                              data-wizard="cell-create-btn"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null,
                      }}
                      InputLabelProps={{
                        shrink: false,
                      }}
                      className="m-0"
                    />

                    {optionsCells.length > 0 && (
                      <Paper className={classes.optionsList}>
                        <MenuList>
                          {optionsCells.map((option, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleSelectCell(option)}
                              className={classes.optionItem}
                            >
                              {option.title}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Paper>
                    )}

                    {loadingCells && optionsCells.length === 0 && (
                      <Paper className={classes.optionsList}>
                        <div className={classes.loadingOption}>
                          <CircularProgress size={20} />
                        </div>
                      </Paper>
                    )}
                  </div>
                )}
              </div>
            </ClickAwayListener>

            <div className="header-separator" />

            {canEditCell && (
              <>
                <Tooltip title="Configurações da Célula">
                  <IconButton
                    className={classes.cellSettingsButton}
                    aria-label="Configurações da Célula"
                    onClick={() => setCellSettingsOpen(true)}
                    data-wizard="cell-edit-btn"
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <LanguageSelector />

            <ThemeSelector variant="header" />

            <div className="header-separator" />

            <Tooltip title={t("logoutTooltip")}>
              <Button
                className={`${classes.logoutButton} text-white`}
                startIcon={<ExitToApp />}
                onClick={handleLogout}
                size="small"
              >
                {t("logout")}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Toolbar>

      {/* Modal de Configurações da Célula */}
      <CellSettingsModal
        open={cellSettingsOpen}
        onClose={() => setCellSettingsOpen(false)}
        onSuccessToast={(msg) => showToast(msg, "success")}
        onErrorToast={(msg) => showToast(msg, "error")}
      />

      {/* Toast */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

export default Header;
